import React from "react";

import {
  CALL_MEDIA_STATES,
  createConsultationTransport,
} from "@/components/appointments/call/consultationSession.js";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import { CALL_STATES } from "@/components/appointments/call/callStates.js";

function resolvePermissionMessage({ transport, t }) {
  const cameraDenied = transport.permissions.camera === CALL_MEDIA_STATES.DENIED;
  const micDenied = transport.permissions.microphone === CALL_MEDIA_STATES.DENIED;

  if (cameraDenied && micDenied) {
    return t(
      "appointments.call.transport.permissions.cameraAndMicDenied",
      "Camera and microphone permission denied."
    );
  }
  if (cameraDenied) {
    return t(
      "appointments.call.transport.permissions.cameraDenied",
      "Camera permission denied."
    );
  }
  if (micDenied) {
    return t(
      "appointments.call.transport.permissions.micDenied",
      "Microphone permission denied."
    );
  }
  return "";
}

function resolveDeviceMessage({ transport, t }) {
  const expectsVideo = transport.kind === "video";
  const cameraState = transport.devices.camera;
  const micState = transport.devices.microphone;

  if (expectsVideo && cameraState === CALL_MEDIA_STATES.MISSING) {
    return t("appointments.call.transport.devices.cameraMissing", "No camera found.");
  }
  if (expectsVideo && cameraState === CALL_MEDIA_STATES.UNAVAILABLE) {
    return t(
      "appointments.call.transport.devices.cameraUnavailable",
      "Camera preview is not available on this device."
    );
  }
  if (micState === CALL_MEDIA_STATES.MISSING) {
    return t("appointments.call.transport.devices.micMissing", "No microphone found.");
  }
  if (micState === CALL_MEDIA_STATES.UNAVAILABLE) {
    return t(
      "appointments.call.transport.devices.micUnavailable",
      "Microphone preview is not available on this device."
    );
  }
  return "";
}

export default function useCallUiState({
  loading = false,
  error = "",
  initialPhase = CALL_STATES.IDLE,
  kind = "audio",
} = {}) {
  const { t } = useLanguage();

  const rootRef = React.useRef(null);
  const clearToastTimerRef = React.useRef(null);

  const [speakerOn, setSpeakerOn] = React.useState(true);
  const [micOn, setMicOn] = React.useState(true);
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [phase, setPhase] = React.useState(initialPhase);
  const [toast, setToast] = React.useState("");
  const [permissions, setPermissions] = React.useState({
    microphone: CALL_MEDIA_STATES.UNKNOWN,
    camera: kind === "video" ? CALL_MEDIA_STATES.UNKNOWN : CALL_MEDIA_STATES.UNAVAILABLE,
  });
  const [devices, setDevices] = React.useState({
    microphone: CALL_MEDIA_STATES.UNKNOWN,
    camera: kind === "video" ? CALL_MEDIA_STATES.UNKNOWN : CALL_MEDIA_STATES.UNAVAILABLE,
  });

  const showToast = React.useCallback((message) => {
    setToast(message);
    if (clearToastTimerRef.current) window.clearTimeout(clearToastTimerRef.current);
    clearToastTimerRef.current = window.setTimeout(() => setToast(""), 3000);
  }, []);

  const resolvePhaseText = React.useCallback(
    (nextPhase) => {
      if (nextPhase === CALL_STATES.CONNECTING) {
        return t("appointments.call.phase.connecting", "Connecting...");
      }
      if (nextPhase === CALL_STATES.RINGING) {
        return t("appointments.call.phase.ringing", "Ringing...");
      }
      if (nextPhase === CALL_STATES.RECONNECTING) {
        return t("appointments.call.phase.reconnecting", "Reconnecting...");
      }
      return "";
    },
    [t]
  );

  React.useEffect(() => {
    return () => {
      if (clearToastTimerRef.current) window.clearTimeout(clearToastTimerRef.current);
    };
  }, []);

  const toggleSpeaker = React.useCallback(
    (onToggle) => {
      setSpeakerOn((current) => {
        const next = !current;
        onToggle?.(next);
        showToast(
          next
            ? t("appointments.call.toast.speakerReady", "Speaker ready.")
            : t("appointments.call.toast.speakerMuted", "Speaker muted for preview.")
        );
        return next;
      });
    },
    [showToast, t]
  );

  const toggleMic = React.useCallback(
    (onToggle) => {
      setMicOn((current) => {
        const next = !current;
        onToggle?.(next);
        showToast(
          next
            ? t("appointments.call.toast.micReady", "Microphone ready.")
            : t("appointments.call.toast.micMuted", "Microphone muted.")
        );
        return next;
      });
    },
    [showToast, t]
  );

  const toggleFullscreen = React.useCallback(async () => {
    const isMobile = window.matchMedia?.("(max-width: 767px)")?.matches ?? true;
    if (isMobile) {
      showToast(t("appointments.call.toast.fullscreenDesktop", "Fullscreen is available on desktop."));
      return;
    }

    try {
      const element = rootRef.current || document.documentElement;
      if (!document.fullscreenElement) await element.requestFullscreen?.();
      else await document.exitFullscreen?.();
    } catch {
      showToast(t("appointments.call.toast.fullscreenUnavailable", "Fullscreen is not available here."));
    }
  }, [showToast, t]);

  const setPermissionState = React.useCallback((target, nextState) => {
    setPermissions((current) => ({
      ...current,
      [target]: nextState,
    }));
  }, []);

  const setDeviceState = React.useCallback((target, nextState) => {
    setDevices((current) => ({
      ...current,
      [target]: nextState,
    }));
  }, []);

  const transport = React.useMemo(
    () =>
      createConsultationTransport({
        kind,
        phase,
        permissions,
        devices,
        media: {
          speakerOn,
          microphoneOn: micOn,
        },
      }),
    [devices, kind, micOn, permissions, phase, speakerOn]
  );

  const transportMessage =
    resolvePermissionMessage({ transport, t }) ||
    resolveDeviceMessage({ transport, t });

  const toastMessage = error
    ? error
    : loading
      ? t("appointments.call.loading", "Loading consultation...")
      : transportMessage || resolvePhaseText(phase) || toast;

  return {
    rootRef,
    speakerOn,
    micOn,
    menuOpen,
    setMenuOpen,
    phase,
    setPhase,
    showToast,
    toggleSpeaker,
    toggleMic,
    toggleFullscreen,
    transport,
    setPermissionState,
    setDeviceState,
    toastMessage,
  };
}
